import { useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { ButtonSlide, CheckboxList, CustomText } from '@vfit/shared/atoms';
import { yupResolver } from '@hookform/resolvers/yup';
import { checkIsApp, useCheckRefund } from '@vfit/consumer/data-access';
import {
  ErrorDetail,
  errorManager,
  ErrorService,
  LoggerInstance,
  resetData,
} from '@vfit/shared/data-access';
import { Form, GenericDetail, TopHeroDynamic, VfModal } from '@vfit/shared/components';
import { useDeviceType } from '@vfit/shared/hooks';
import { useForm } from 'react-hook-form';
import {
  Container,
  FormContainer,
  InputContainer,
  ButtonContainer,
  H1,
  RadioListContainer,
  H2,
  Subtitle,
} from './billingPage.style';
import { IBillingInputs, IProps } from './billingPage.models';
import { validationSchema } from './billingPage.validation';

const BillingPage = ({ fatturazione2830, languagelist, heroslider }: IProps) => {
  const { slides } = heroslider || {};
  const CONTACT_NUM_ID = languagelist?.elements?.find((element) => element.id === 'voucher')?.id;
  const { elements: options } = languagelist || {};
  const queryClient = useQueryClient();
  const { isTablet, isDesktop } = useDeviceType();
  const [fixedLine, setFixedLine] = useState<string>('');
  const [reqType, setReqType] = useState<string>(options?.[0]?.id || '');
  const [contactNumber, setContactNumber] = useState('');
  const [showContactInput, setShowContactInput] = useState<boolean>(false);
  const [refetchData, setRefetchData] = useState<boolean>(false);
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    text: string;
    content: string;
    buttonLabel: string;
  }>({
    isOpen: false,
    text: '',
    content: '',
    buttonLabel: '',
  });
  const {
    register,
    getValues,
    reset,
    formState: { isValid, errors },
  } = useForm<IBillingInputs>({
    resolver: yupResolver(validationSchema()),
    mode: 'onChange',
  });

  const errorGeneric: ErrorDetail = {
    message: 'Si è verificato un errore.',
    title: 'Ops',
    actionEvent: () => {},
    actionText: 'Riprova',
  };

  const {
    isSuccess,
    isError,
    data: checkRefundResponse,
    error: checkRefundError,
    remove,
  } = useCheckRefund(fixedLine, reqType, contactNumber, { enabled: refetchData });

  const setModalHeight = () => (isTablet && 331) || (isDesktop && 370) || undefined;

  const handleCloseModal = () => {
    setFixedLine('');
    setContactNumber('');
    reset();
    setRefetchData(false);
    setModalConfig({
      isOpen: false,
      text: '',
      content: '',
      buttonLabel: '',
    });
  };

  const handleResetQuery = () => {
    resetData(queryClient, ['checkRefund']);
    remove();
  };

  const onChange = (e: { title: string; value: string }) => {
    setReqType(e?.value || '');
    setShowContactInput(() => (e?.value === CONTACT_NUM_ID ? true : false));
  };

  useEffect(() => {
    if (!modalConfig.isOpen) {
      handleResetQuery();
    }
  }, [modalConfig.isOpen]);

  useEffect(() => {
    if (isSuccess) {
      switch (checkRefundResponse.eligible) {
        case true:
          setModalConfig({
            isOpen: true,
            text: fatturazione2830?.modals?.success?.title || '',
            content: fatturazione2830?.modals?.success?.description || '',
            buttonLabel: fatturazione2830?.formLabels?.bottonLabel || '',
          });
          break;
        case false:
          if (!!checkRefundResponse.key && checkRefundResponse.key === 'already_requested') {
            setModalConfig({
              isOpen: true,
              text: fatturazione2830?.modals?.error?.title || '',
              content: fatturazione2830?.modals?.error?.descriptionAlreadyRequested || '',
              buttonLabel: fatturazione2830?.formLabels?.bottonLabel || '',
            });
          }
          if (!!checkRefundResponse.key && checkRefundResponse.key === 'not_found') {
            setModalConfig({
              isOpen: true,
              text: fatturazione2830?.modals?.error?.title || '',
              content: fatturazione2830?.modals?.error?.descriptionNotFound || '',
              buttonLabel: fatturazione2830?.formLabels?.bottonLabel || '',
            });
          }
          break;
        default:
          break;
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      LoggerInstance.error(isError, checkRefundError.statusText);
      errorManager.handleError(ErrorService.getSeverityErrorMedium(), errorGeneric);
      handleResetQuery();
    }
  }, [isError]);

  const handleSubmit = () => {
    const inputs = getValues();
    if (reqType === CONTACT_NUM_ID) setContactNumber(inputs?.contactNumber);
    setFixedLine(inputs?.fixedLineNumber);
    setRefetchData(true);
  };

  return (
    <div>
      <Container>
        {slides?.[0]?.image && (
          <TopHeroDynamic
            image={slides?.[0]?.image}
            imageMobile={slides?.[0]?.imageMobile || slides?.[0]?.image}
            isShort
            title={slides?.[0]?.title}
          />
        )}

        <FormContainer>
          <H2
            dangerouslySetInnerHTML={{
              __html:
                fatturazione2830?.pageTexts?.title ||
                `Delibera AgCom 269/18/CONS<br> clienti di rete fissa e convergenti, richiesta di restituzione giorni erosi`,
            }}
          />
          <Subtitle
            dangerouslySetInnerHTML={{
              __html:
                fatturazione2830?.pageTexts?.top ||
                `<p>
    In seguito al ritorno alla fatturazione mensile delle offerte di Rete Fissa e convergenti, in base alla delibera AgCom 269/18/CONS,
    <strong>è possibile richiedere la restituzione dei giorni erosi</strong> nel periodo decorrente dal 23 giugno 2017 fino al ritorno alla fatturazione su base mensile (5 aprile 2018).
  </p>

  <p>In alternativa, puoi scegliere <strong>i vantaggi messi a disposizione dai nostri Partner:</strong></p>
  <ul>
    <li>10€ di sconto su spesa minima di 60€ con <strong>Winelivery</strong>;</li>
    <li>Buono carburante di 5€ valido su un rifornimento minimo di 30€ nei distributori <strong>Q8 e Q8easy</strong>;</li>
    <li>7% su una selezione esclusiva di Hotel su <strong>lastminute.com</strong>;</li>
    <li>Sconto del 15% su una spesa di 49€ con <strong>Dr. Max</strong>.</li>
  </ul>

  <p>E se sei ancora cliente Vodafone, insieme agli sconti dei partner, riceverai anche 
  <strong>l'attivazione gratuita dei seguenti servizi Vodafone:</strong></p>
  <ul>
    <li><strong>Rete Sicura Family</strong>, il servizio di sicurezza digitale che ti protegge dalle minacce online;</li>
    <li><strong>Chiamate illimitate</strong> verso tutti i numeri fissi e mobili nazionali, dal tuo telefono di casa;</li>
    <li><strong>150 Giga sulla SIM Dati</strong> associata alla Rete Fissa.</li>
  </ul>`,
            }}
          />

          <H1
            dangerouslySetInnerHTML={{
              __html: fatturazione2830?.formLabels?.label || 'Inserisci i tuoi dati',
            }}
          />
          <InputContainer>
            <div className="container">
              <Form.TextInput
                label={fatturazione2830?.formLabels?.input || ''}
                error={errors.fixedLineNumber?.message}
                {...register('fixedLineNumber')}
              />
            </div>
          </InputContainer>
          {options && options?.length > 0 && (
            <InputContainer>
              <RadioListContainer>
                <CheckboxList
                  passedValueIndex={0}
                  onChangeItem={onChange}
                  items={options?.map((e) => ({
                    title: e.value || '',
                    value: e.id || '',
                  }))}
                />
              </RadioListContainer>
            </InputContainer>
          )}
          {showContactInput && (
            <InputContainer>
              <div className="container">
                <Form.MaskInput
                  mask={'+3\\9 99999999999'}
                  label={
                    fatturazione2830?.formLabels?.contactNumberLabel || 'Numero di contatto mobile'
                  }
                  placeholder=" "
                  {...register('contactNumber', {
                    setValueAs: (v: string) => v.trim().substring(4),
                  })}
                />
              </div>
            </InputContainer>
          )}
          <ButtonContainer>
            <ButtonSlide
              onClick={handleSubmit}
              disabled={!isValid}
              disabledButtonColor="#bebebe"
              disabledBorderColor="#bebebe"
              disabledTextColor="#fff"
              isApp={checkIsApp()}
              label={fatturazione2830?.formLabels?.bottonLabel || ''}
              buttonColor="#e60000"
              labelColor="#fff"
              hoverColor="#bd0000"
              clickColor="#a10000"
              name="action_billingPage"
            />
          </ButtonContainer>
          <CustomText
            text={fatturazione2830?.pageTexts?.bottom || ''}
            size={16}
            lineHeight={22}
            textAlign="center"
          />
        </FormContainer>
      </Container>
      <VfModal height={setModalHeight()} isOpen={modalConfig.isOpen} handleClose={handleCloseModal}>
        <GenericDetail
          title={modalConfig.text}
          description={modalConfig.content}
          secondButtonLabel={modalConfig.buttonLabel}
          options={{
            size: 18,
            lineHeight: 24,
            sizeMobile: 20,
            lineHeightMobile: 26,
            isModal: true,
            textAlign: 'left',
          }}
        />
      </VfModal>
    </div>
  );
};

export default BillingPage;
